import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { LoadingState } from '@/components/ui/LoadingState';
import { Modal } from '@/components/ui/Modal/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import { Badge } from '@/components/ui/Badge';
import { X, Plus } from 'lucide-react';
import { t, __resolve } from '@/lib/i18n';
import {
  useRorosIcpPersonaLinks,
  useRorosIcpPersonaLinkCreate,
  useRorosIcpPersonaLinkRemove,
  useRorosPersonas,
  useRorosIcps,
} from '@/hooks/roros/overarching';

interface IcpPersonaLinksPanelProps {
  icpId?: string;
  personaId?: string;
  notes?: boolean;
}

/**
 * Shows linked entities for one side of the ICP ↔ Persona relationship.
 * - When `icpId` is given: shows linked Personas, lets user add/remove persona links.
 * - When `personaId` is given: shows linked ICPs, lets user add/remove ICP links.
 */
export function IcpPersonaLinksPanel({ icpId, personaId, notes = false }: IcpPersonaLinksPanelProps) {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ id: string; name: string } | null>(null);

  // Determine which side we're on
  const linkParams = icpId ? { icpId } : personaId ? { personaId } : {};
  const linksQuery = useRorosIcpPersonaLinks(linkParams);
  const createLink = useRorosIcpPersonaLinkCreate();
  const removeLink = useRorosIcpPersonaLinkRemove();

  // Fetch the "other side" list for the add modal
  const personasQuery = useRorosPersonas();
  const icpsQuery = useRorosIcps();

  const links = (linksQuery.data ?? []) as unknown as Array<Record<string, unknown>>;
  const linkedIds = new Set(links.map((l) => (icpId ? l.personaId : l.icpId) as string));

  // Build the options list for the add modal
  const availableOptions = (() => {
    if (icpId) {
      // Adding personas — filter out already-linked ones
      const personas = (personasQuery.data ?? []) as unknown as Array<{ id: string; name: string }>;
      return personas.filter((p) => !linkedIds.has(p.id));
    } else {
      // Adding ICPs — filter out already-linked ones
      const icps = (icpsQuery.data ?? []) as unknown as Array<{ id: string; name: string }>;
      return icps.filter((c) => !linkedIds.has(c.id));
    }
  })();

  const panelLabel = icpId ? t('Linked Personas') : t('Linked ICPs');
  const addLabel = icpId ? t('Link Persona') : t('Link ICP');

  function handleAddLink(otherId: string, otherName: string) {
    const payload: Record<string, unknown> = {};
    if (icpId) {
      payload.icpId = icpId;
      payload.personaId = otherId;
    } else {
      payload.icpId = otherId;
      payload.personaId = personaId;
    }
    createLink.mutate(payload, {
      onSuccess: () => {
        showToast.success(__resolve('ui::{{name}} linked', { name: otherName }));
        setIsAddOpen(false);
      },
      onError: (err: Error) => {
        showToast.error(err.message || t('Failed to create link'));
      },
    });
  }

  function handleRemove() {
    if (!removeTarget) return;
    removeLink.mutate(removeTarget.id, {
      onSuccess: () => {
        showToast.success(t('Link removed'));
        setRemoveTarget(null);
      },
      onError: (err: Error) => {
        showToast.error(err.message || t('Failed to remove link'));
      },
    });
  }

  // Get display name for a link item
  function getLinkDisplayName(link: Record<string, unknown>): string {
    if (icpId) {
      // link has personaId — we need the persona name
      // Try embedded name first (if the backend returns it), else fall back to Unnamed
      return (link.personaName as string) || t('Unnamed');
    } else {
      return (link.icpName as string) || t('Unnamed');
    }
  }

  return (
    <Card padding="md" className="mt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground">{panelLabel}</h3>
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setIsAddOpen(true)}
        >
          {t('Add link')}
        </Button>
      </div>

      {linksQuery.isLoading ? (
        <LoadingState size="sm" minHeight="60px" />
      ) : links.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {icpId ? t('No personas linked yet.') : t('No ICPs linked yet.')}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {links.map((link) => {
            const displayName = getLinkDisplayName(link);
            const linkId = link.id as string;
            return (
              <Badge key={linkId} variant="secondary" className="flex items-center gap-1 pr-1">
                <span>{displayName}</span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setRemoveTarget({ id: linkId, name: displayName })}
                  aria-label={__resolve('ui::Remove link to {{name}}', { name: displayName })}
                  className="ml-1 rounded-full hover:bg-muted p-0.5 transition-colors"
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Add link modal */}
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title={addLabel}
      >
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {availableOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {icpId ? t('No personas available to link.') : t('No ICPs available to link.')}
            </p>
          ) : (
            availableOptions.map((opt) => (
              <Button
                key={opt.id}
                type="button"
                variant="ghost"
                className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors text-sm"
                onClick={() => handleAddLink(opt.id, opt.name)}
                disabled={createLink.isPending}
              >
                {opt.name}
              </Button>
            ))
          )}
        </div>
      </Modal>

      {/* Confirm remove dialog */}
      <ConfirmDialog
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemove}
        title={t('Remove link')}
        description={__resolve('ui::Are you sure you want to remove the link to "{{name}}"?', { name: removeTarget?.name ?? '' })}
        variant="destructive"
        confirmText={t('Remove')}
        isLoading={removeLink.isPending}
      />
    </Card>
  );
}
